import { useAuth } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { OnboardingFlags, OrgFlags, OrgFlagsSettingsResponse } from "../types.ts";
import { useApi } from "./use-api.ts";

/**
 * Storage-key prefix for the per-org flags localStorage cache.
 * Scoped per org so org-switching doesn't leak state.
 *
 * NOTE: Kept separate from the existing `desplega-config-<orgId>` bucket
 * (device-level user preferences like `sidebarCollapsed`) to avoid
 * collisions — flags mirror server state, preferences are client-only.
 */
const FLAGS_STORAGE_PREFIX = "desplega-org-flags";

const DEFAULT_ONBOARDING_FLAGS: OnboardingFlags = {
  completed_at: null,
  skipped: false,
  completion_reason: null,
};

const DEFAULT_ORG_FLAGS: OrgFlags = {
  onboarding: { ...DEFAULT_ONBOARDING_FLAGS },
};

const flagsStorageKey = (orgId: string | null | undefined) =>
  `${FLAGS_STORAGE_PREFIX}-${orgId ?? "anonymous"}`;

/**
 * Read the cached `OrgFlags` for an org out of localStorage.
 * Returns undefined if no cache / parse error / no orgId (so react-query
 * treats it as a genuine first-load and shows `isLoading`).
 */
function readCachedFlags(orgId: string | null | undefined): OrgFlags | undefined {
  if (!orgId) return undefined;
  try {
    const raw = localStorage.getItem(flagsStorageKey(orgId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as OrgFlags;
    // Shallow shape guard — minimal validation, trust-on-read
    if (!parsed || typeof parsed !== "object" || !parsed.onboarding) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

function writeCachedFlags(orgId: string | null | undefined, flags: OrgFlags) {
  if (!orgId) return;
  try {
    localStorage.setItem(flagsStorageKey(orgId), JSON.stringify(flags));
  } catch {
    // Quota exceeded / privacy mode — ignore.
  }
}

interface PatchFlagsBody {
  patch: Partial<OrgFlags> & Record<string, unknown>;
}

/**
 * Hook over `GET /client/org/settings` (+ PATCH /flags) with a localStorage
 * cache for instant first-paint. The DB is the source of truth; localStorage
 * is a read-through cache (primes `placeholderData`, written back on every
 * successful fetch / mutation).
 *
 * Exposes:
 *  - `flags`         : OrgFlags | undefined  (undefined while loading & no cache)
 *  - `onboarding`    : OnboardingFlags       (defaults when no flags loaded yet)
 *  - `isCompleted`   : onboarding.completed_at != null (derived)
 *  - `isLoading`     : true iff no cache + no fresh fetch yet
 *  - `patchFlags`    : mutation (optimistic); rolls back on error
 *  - `error`         : last fetch error (if any)
 *
 * Org-switch handling: the react-query `queryKey` is keyed on `orgId`, so the
 * cache is scoped per-org automatically.
 */
export function useOrgFlags() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { callApi } = useApi<OrgFlagsSettingsResponse>();
  const { callApi: callApiPatch } = useApi<OrgFlagsSettingsResponse>();

  const queryKey = useMemo(() => ["org-flags", orgId] as const, [orgId]);

  const query = useQuery<OrgFlagsSettingsResponse>({
    queryKey,
    queryFn: async () => callApi("/client/org/settings"),
    enabled: !!orgId,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    // Localstorage cache → instant first-paint.
    placeholderData: () => {
      const cached = readCachedFlags(orgId);
      return cached ? { flags: cached } : undefined;
    },
  });

  // Persist fresh server state back to localStorage whenever the query data
  // updates. `useEffect` would also work but a derived write here keeps the
  // cache in-sync without an extra render.
  const fresh = query.data?.flags;
  if (fresh && !query.isPlaceholderData) {
    writeCachedFlags(orgId, fresh);
  }

  const mutation = useMutation<
    OrgFlagsSettingsResponse,
    Error,
    Partial<OrgFlags> & Record<string, unknown>,
    { previous: OrgFlagsSettingsResponse | undefined }
  >({
    mutationKey: ["org-flags-patch", orgId],
    mutationFn: async (patch) => {
      const body: PatchFlagsBody = { patch };
      return callApiPatch("/client/org/settings/flags", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    // Optimistic update: write the merged state to the react-query cache
    // AND localStorage immediately so the UI reflects the change without a
    // round-trip. Roll back if the server rejects.
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<OrgFlagsSettingsResponse>(queryKey);
      const base = previous?.flags ?? DEFAULT_ORG_FLAGS;
      const merged = deepMergeFlags(base, patch);
      const next: OrgFlagsSettingsResponse = { flags: merged };
      queryClient.setQueryData(queryKey, next);
      writeCachedFlags(orgId, merged);
      return { previous };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(queryKey, ctx.previous);
        writeCachedFlags(orgId, ctx.previous.flags);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      writeCachedFlags(orgId, data.flags);
    },
  });

  const patchFlags = useCallback(
    (patch: Partial<OrgFlags> & Record<string, unknown>) => mutation.mutateAsync(patch),
    [mutation],
  );

  const flags = query.data?.flags;
  const onboarding: OnboardingFlags = flags?.onboarding ?? DEFAULT_ONBOARDING_FLAGS;
  const isCompleted = onboarding.completed_at != null;

  // We're "loading" only if there's no data at all yet. Once react-query has
  // *any* data (cache placeholder or fresh), we can render decisively.
  const isLoading = !query.data && query.isLoading;

  return {
    flags,
    onboarding,
    isCompleted,
    isLoading,
    error: query.error,
    patchFlags,
    isPatching: mutation.isPending,
  };
}

/**
 * Shallow+one-level deep merge for the optimistic patch. We only ever patch
 * `onboarding` subtree in practice, so a 1-level deep merge is enough.
 * Keeps semantics parallel to the BE's `_deep_merge` (patch values overwrite).
 */
function deepMergeFlags(
  base: OrgFlags,
  patch: Partial<OrgFlags> & Record<string, unknown>,
): OrgFlags {
  const out: Record<string, unknown> = {
    ...base,
    onboarding: { ...base.onboarding },
  };
  for (const [k, v] of Object.entries(patch)) {
    const existing = out[k];
    if (
      typeof v === "object" &&
      v !== null &&
      !Array.isArray(v) &&
      typeof existing === "object" &&
      existing !== null &&
      !Array.isArray(existing)
    ) {
      out[k] = {
        ...(existing as Record<string, unknown>),
        ...(v as Record<string, unknown>),
      };
    } else {
      out[k] = v;
    }
  }
  return out as unknown as OrgFlags;
}
